import { useEffect, useRef, useState } from "react";
import { Check, RotateCcw, SkipForward } from "lucide-react";

/**
 * Full-screen overlay that asks the customer to draw a circle around their
 * face. The circle position + radius are returned as normalized (0..1)
 * coords — resolution-independent so the Mirror can composite the face at
 * the same location on Gemini's output.
 *
 * Interaction (mobile-first):
 *  • Tap anywhere on the photo → recenter the circle at that point.
 *  • Drag → live-move the circle.
 *  • Slider at the bottom → adjust radius.
 *  • Skip → proceed without face-mask (falls back to prompt-only preservation).
 *  • Confirm → onDone({x_pct, y_pct, r_pct}).
 */
export default function FaceMaskDrawer({ photo, onDone, onSkip }) {
    const wrapperRef = useRef(null);
    const [dims, setDims] = useState({ w: 0, h: 0 });
    // Sensible default: top-center of the photo (where faces usually are).
    const [cx, setCx] = useState(0.5);
    const [cy, setCy] = useState(0.3);
    const [r, setR] = useState(0.18);
    const dragging = useRef(false);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return undefined;
        const ro = new ResizeObserver(() => {
            const rect = el.getBoundingClientRect();
            setDims({ w: rect.width, h: rect.height });
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const relPoint = (evt) => {
        const el = wrapperRef.current;
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        const clientX = evt.touches ? evt.touches[0].clientX : evt.clientX;
        const clientY = evt.touches ? evt.touches[0].clientY : evt.clientY;
        const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
        return { x, y };
    };

    const onPointer = (evt) => {
        const p = relPoint(evt);
        if (!p) return;
        setCx(p.x);
        setCy(p.y);
    };

    const onPointerDown = (evt) => {
        evt.preventDefault();
        dragging.current = true;
        onPointer(evt);
    };

    const onPointerMove = (evt) => {
        if (!dragging.current) return;
        evt.preventDefault();
        onPointer(evt);
    };

    const onPointerUp = () => {
        dragging.current = false;
    };

    // Circle overlay in CSS pixels
    const pxCx = cx * dims.w;
    const pxCy = cy * dims.h;
    // Radius is normalized to the shorter dimension so the drawn size matches
    // what the compositor will use.
    const S = Math.min(dims.w, dims.h);
    const pxR = r * S;

    const confirm = () => {
        onDone({ x_pct: cx, y_pct: cy, r_pct: r });
    };

    return (
        <div
            className="fixed inset-0 z-[80] bg-black flex flex-col"
            data-testid="face-mask-drawer"
        >
            {/* Title bar */}
            <div className="shrink-0 px-5 pt-5 pb-3 text-center">
                <div className="editorial-eyebrow text-brand-bronze">
                    <span className="inline-block w-6 h-px bg-brand-bronze align-middle mr-2.5" />
                    Step 2 of 2 · Face fidelity
                </div>
                <h1 className="font-display text-white text-2xl sm:text-3xl mt-2 tracking-editorial">
                    Draw a circle around your face
                </h1>
                <p className="text-white/60 text-xs sm:text-sm mt-2 max-w-md mx-auto">
                    We&apos;ll composite your original face pixel-for-pixel onto every try-on.
                    Tap where your face is, then use the slider to size the circle.
                </p>
            </div>

            {/* Photo + mask canvas */}
            <div
                ref={wrapperRef}
                className="relative flex-1 overflow-hidden touch-none select-none"
                onMouseDown={onPointerDown}
                onMouseMove={onPointerMove}
                onMouseUp={onPointerUp}
                onMouseLeave={onPointerUp}
                onTouchStart={onPointerDown}
                onTouchMove={onPointerMove}
                onTouchEnd={onPointerUp}
                data-testid="face-mask-canvas"
            >
                <img
                    src={photo}
                    alt="Your photo"
                    className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                    draggable={false}
                />
                {/* Dim scrim outside the circle. Uses an SVG mask so the circle is a clean cutout. */}
                {dims.w > 0 && dims.h > 0 ? (
                    <svg
                        viewBox={`0 0 ${dims.w} ${dims.h}`}
                        preserveAspectRatio="none"
                        className="absolute inset-0 w-full h-full pointer-events-none"
                    >
                        <defs>
                            <mask id="face-hole">
                                <rect width="100%" height="100%" fill="white" />
                                <circle cx={pxCx} cy={pxCy} r={pxR} fill="black" />
                            </mask>
                        </defs>
                        <rect
                            width="100%" height="100%"
                            fill="rgba(0,0,0,0.55)"
                            mask="url(#face-hole)"
                        />
                        <circle
                            cx={pxCx} cy={pxCy} r={pxR}
                            fill="none"
                            stroke="#C6A87C"
                            strokeWidth="2.5"
                            strokeDasharray="6 6"
                        />
                        <circle
                            cx={pxCx} cy={pxCy} r={pxR + 6}
                            fill="none"
                            stroke="rgba(198,168,124,0.35)"
                            strokeWidth="1"
                        />
                    </svg>
                ) : null}
            </div>

            {/* Radius slider + actions */}
            <div className="shrink-0 px-5 pb-6 pt-3 space-y-3 bg-gradient-to-t from-black/95 to-transparent">
                <label className="flex items-center gap-3 text-white/80 text-xs font-mono uppercase tracking-wider-2">
                    <span className="w-8 shrink-0">Size</span>
                    <input
                        type="range"
                        min="0.08"
                        max="0.35"
                        step="0.005"
                        value={r}
                        onChange={(e) => setR(parseFloat(e.target.value))}
                        className="w-full accent-brand-bronze"
                        data-testid="face-mask-radius"
                    />
                </label>
                <div className="flex gap-2 justify-center flex-wrap">
                    <button
                        type="button"
                        onClick={() => { setCx(0.5); setCy(0.3); setR(0.18); }}
                        className="px-4 py-2.5 text-xs font-mono uppercase tracking-wider-2 text-white/70 border border-white/20 rounded-full hover:bg-white/10 transition"
                        data-testid="face-mask-reset"
                    >
                        <RotateCcw className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={1.5} />
                        Reset
                    </button>
                    <button
                        type="button"
                        onClick={onSkip}
                        className="px-4 py-2.5 text-xs font-mono uppercase tracking-wider-2 text-white/70 border border-white/20 rounded-full hover:bg-white/10 transition"
                        data-testid="face-mask-skip"
                    >
                        <SkipForward className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={1.5} />
                        Skip
                    </button>
                    <button
                        type="button"
                        onClick={confirm}
                        className="px-6 py-2.5 text-xs font-mono uppercase tracking-wider-2 bg-brand-bronze text-white rounded-full hover:bg-brand-ink transition"
                        data-testid="face-mask-confirm"
                    >
                        <Check className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={1.5} />
                        Lock face
                    </button>
                </div>
            </div>
        </div>
    );
}
